import { useState } from "react";
import { toast } from "react-toastify";

// the validationRules structure should be like this
// const validation = {
//   name: { required: true, message: "Name is required" },
//   options: {
//     required: true,
//     type: "arrayValidation",
//     message: "All options must have a title",
//     validationField: ["fieldName1","fieldName2",...],// you can add this for validation under an array or if you not include validation always check on empty array
//   },
//   email: [
//     { required: true, message: "Email is required" },or
//     { type: "email", errorType: "info", message: "This is not email" },or
//     { type: "enum", enum: ["yes","no"], message: "This is not email" },or
//     {
//       type: "range"||"number",
//       errorType: "info",
//       max: 5,
//       message: "",
//     },
//   ],
//   password: { required: true, message: "Password is required" },
//   confirmPassword: { required: true, message: "Confirm password is required" },
// };

// Custom hook for form validation
const useForm = ({
  initialState = {},
  validationRules = {},
  showToast = false,
  steper = false,
} = {}) => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});

  const setFieldsValue = (data) => {
    if (typeof data === "object" && !Array.isArray(data)) {
      Object.keys(data)?.forEach((key) => {
        setValues((pre) => ({ ...pre, [key]: data[key] }));
      });
    } else {
      console.error("Update data is not an object");
    }
  };

  const handleChange = (e) => {
    let element = e.target.type;
    const { name, value, checked, files } = e.target;

    if (steper) {
      setErrors({});
    }

    switch (element) {
      case "checkbox":
        setValues({
          ...values,
          [name]: checked,
        });
        break;
      case "radio":
        setValues({
          ...values,
          [name]: checked,
        });
        break;
      case "file":
        const multiple = e.target.multiple;

        if (multiple) {
          setValues({
            ...values,
            [name]: files,
          });
        } else {
          setValues({
            ...values,
            [name]: files[0],
          });
        }
        e.target.value = null;
        break;
      default:
        const condition = validationRules?.[name];
        if (
          condition &&
          condition?.type &&
          condition?.type?.toLowerCase() === "number"
        ) {
          const value_type = typeof value;
          const num_value = Number(value);
          if (!isNaN(num_value)) {
            setValues({
              ...values,
              [name]: value_type === "number" ? value : String(Number(value)),
            });
          }
        } else {
          setValues({
            ...values,
            [name]: value,
          });
        }
        break;
    }

    // Validate the field
    const newErrors = validateField(
      name,
      typeof value == "string" ? value.trim() : value
    );

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: newErrors[name], // Update error for this field
    }));
  };

  const validateField = (fieldName, value) => {
    const fieldErrors = {};

    // ARRAY VALIDATION for title fields inside an array
    if (Array.isArray(value)) {
      const field = validationRules[fieldName];
      const errorsInArray = [];

      if (!field?.validationField && value.length === 0) {
        errorsInArray.push({
          message: field?.message || `This is required`,
          type: "required",
        });
      }

      value.forEach((item, index) => {
        field?.validationField?.forEach((fieldKey) => {
          if (!item?.[fieldKey]) {
            errorsInArray.push({
              index,
              field: fieldKey,
              message: `${fieldKey} at position ${index + 1} is required`,
              type: "required",
            });
          }
        });
      });

      if (errorsInArray.length > 0) {
        fieldErrors[fieldName] = {
          message: errorsInArray.map((e) => e.message).join(", "),
          type: "arrayValidation",
          details: errorsInArray,
        };
        return fieldErrors;
      }
    }

    const rules = validationRules[fieldName];

    if (Array.isArray(rules) && rules?.length > 0) {
      for (const rule of rules) {
        if (rule.required && !value) {
          fieldErrors[fieldName] = {
            message: rule.message || `${fieldName} is required`,
            type: rule?.errorType || "required",
          };
          return fieldErrors;
        }

        switch (rule.type) {
          case "email":
            if (
              value &&
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
            ) {
              fieldErrors[fieldName] = {
                message: rule.message || `${fieldName} is not a valid email`,
                type: rule?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "range":
            if (value && value.length > rule?.max) {
              fieldErrors[fieldName] = {
                message: rule.message || `Invalid range`,
                type: rule?.errorType || "required",
              };
              setValues((pre) => ({
                ...pre,
                [fieldName]: value?.slice(0, rule?.max),
              }));
              return fieldErrors;
            }
            if (value && value.length < rule?.min) {
              fieldErrors[fieldName] = {
                message: rule.message || `Invalid range`,
                type: rule?.errorType || "required",
              };
              setValues((pre) => ({
                ...pre,
                [fieldName]: value?.slice(0, rule?.min),
              }));
              return fieldErrors;
            }
            break;

          case "link":
            const linkRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
            if (value && !linkRegex.test(value)) {
              fieldErrors[fieldName] = {
                message: rule.message || `${fieldName} is not a valid link`,
                type: rule?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "enum":
            if (rule.enum && !rule.enum.includes(value)) {
              fieldErrors[fieldName] = {
                message:
                  rule.message ||
                  `${fieldName} must be one of ${rule.enum.join(", ")}`,
                type: rule?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "password":
            const minLength = rule.minLength || 8;
            const passwordRegex = new RegExp(
              rule?.regex ||
                `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};':"\\\\|,.<>/?]).{${minLength},}$`
            );

            if (value && !passwordRegex.test(value)) {
              fieldErrors[fieldName] = {
                message:
                  rule.message ||
                  `${fieldName} must be at least ${minLength} characters long and include uppercase, lowercase, number, and special character`,
                type: rule?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "number":
            const val = Number(value);
            if (isNaN(val)) {
              fieldErrors[fieldName] = {
                message: rule.message || `accepted only number (0-9)`,
                type: rule?.errorType || "required",
              };
              return fieldErrors;
            }
            if (!isNaN(val) && rule?.min !== undefined && val < rule?.min) {
              fieldErrors[fieldName] = {
                message:
                  rule.message ||
                  `${fieldName} must be greater then ${rule?.min}`,
                type: rule?.errorType || "required",
              };
              return fieldErrors;
            }
            if (!isNaN(val) && rule?.max !== undefined && val > rule?.max) {
              fieldErrors[fieldName] = {
                message:
                  rule.message || `${fieldName} must be less then ${rule?.max}`,
                type: rule?.errorType || "required",
              };
              return fieldErrors;
            }
            break;
          default:
            break;
        }
      }
    } else if (rules && Object.keys(rules)?.length > 0) {
      if (rules.required && !value) {
        fieldErrors[fieldName] = {
          message: rules.message || `${fieldName} is required`,
          type: rules?.errorType || "required",
        };
        return fieldErrors;
      } else {
        switch (rules?.type) {
          case "email":
            if (
              value &&
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
            ) {
              fieldErrors[fieldName] = {
                message: rules.message || `${fieldName} is not a valid email`,
                type: rules?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "range":
            const range_val = Number(value);
            if (range_val && range_val > rules?.max) {
              fieldErrors[fieldName] = {
                message: rules.message || `Invalid range`,
                type: rules?.errorType || "required",
              };
              setValues((pre) => ({
                ...pre,
                [fieldName]: rules?.max,
              }));
              return fieldErrors;
            }
            if (range_val && range_val < rules?.min) {
              fieldErrors[fieldName] = {
                message: rules.message || `Invalid range`,
                type: rules?.errorType || "required",
              };
              setValues((pre) => ({
                ...pre,
                [fieldName]: rules?.min,
              }));
              return fieldErrors;
            }
            break;

          case "link":
            const linkRegex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
            if (value && !linkRegex.test(value)) {
              fieldErrors[fieldName] = {
                message: rules.message || `${fieldName} is not a valid link`,
                type: rules?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "enum":
            if (rules.enum && !rules.enum.includes(value)) {
              fieldErrors[fieldName] = {
                message:
                  rules.message ||
                  `${fieldName} must be one of ${rules.enum.join(", ")}`,
                type: rules?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "password":
            const minLength = rules.minLength || 8;
            const passwordRegex = new RegExp(
              rules?.regex ||
                `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};':"\\\\|,.<>/?]).{${minLength},}$`
            );

            if (value && !passwordRegex.test(value)) {
              fieldErrors[fieldName] = {
                message:
                  rules.message ||
                  `${fieldName} must be at least ${minLength} characters long and include uppercase, lowercase, number, and special character`,
                type: rules?.errorType || "required",
              };
              return fieldErrors;
            }
            break;

          case "number":
            const val = Number(value);
            if (isNaN(val)) {
              fieldErrors[fieldName] = {
                message: rules.message || `accepted only number`,
                type: rules?.errorType || "required",
              };
              return fieldErrors;
            }
            if (!isNaN(val) && rules?.min !== undefined && val < rules?.min) {
              fieldErrors[fieldName] = {
                message:
                  rules.message ||
                  `${fieldName} must be greater then ${rules?.min}`,
                type: rules?.errorType || "required",
              };
              setValues((pre) => ({
                ...pre,
                [fieldName]: rules?.min,
              }));
              return fieldErrors;
            }
            if (!isNaN(val) && rules?.max !== undefined && val > rules?.max) {
              fieldErrors[fieldName] = {
                message:
                  rules.message ||
                  `${fieldName} must be less then ${rules?.max}`,
                type: rules?.errorType || "required",
              };
              setValues((pre) => ({
                ...pre,
                [fieldName]: rules?.max,
              }));
              return fieldErrors;
            }
            break;
          default:
            break;
        }
      }
    }

    return fieldErrors; // Empty if no error
  };

  const handleSubmit = (e, onSubmit = () => {}) => {
    e?.preventDefault();
    const fieldsValues = {};

    if (values) {
      Object.keys(values)?.forEach((key) => {
        if (values[key] && typeof values[key] === "string") {
          fieldsValues[key] = values[key]?.trim();
        } else {
          fieldsValues[key] = values[key];
        }
      });
    }

    let formErrors = {};

    // Validate required fields
    Object.keys(validationRules)?.forEach((key) => {
      if (Array.isArray(validationRules[key])) {
        const requiredRule = validationRules[key].find((rule) => rule.required);
        if (requiredRule && !fieldsValues[key]) {
          formErrors[key] = {
            message: requiredRule.message || `${key} is required`,
            type: requiredRule?.errorType || "required",
          };
        }
      } else if (validationRules[key]?.required && !fieldsValues[key]) {
        formErrors[key] = {
          message: validationRules[key].message || `${key} is required`,
          type: validationRules[key]?.errorType || "required",
        };
      }
    });

    // Validate non-required fields (only if they have a value)
    Object.keys(fieldsValues)?.forEach((key) => {
      if (fieldsValues[key] && validationRules[key]) {
        const fieldErrors = validateField(key, fieldsValues[key]);
        if (Object.keys(fieldErrors).length > 0) {
          formErrors[key] = fieldErrors[key];
        } else {
          delete formErrors[key]; // Remove error if value is valid
        }
      }
    });

    // Confirm password validation
    if (values.password && values.confirmPassword) {
      if (values.password !== values.confirmPassword) {
        formErrors.confirmPassword = {
          message: "Passwords not matched",
          type: "required",
        };
      }
    }

    if (showToast) {
      if (Object.keys(formErrors)?.length > 0) {
        toast.warning("Please provide values for all required fields.");
      }
    }
    setErrors(formErrors);

    // Approve submission only if there are no errors
    if (Object.keys(formErrors).length === 0) {
      setErrors({});
      onSubmit(values);
    }
  };

  const validateForm = (values) => {
    const formErrors = {};
    for (const key in validationRules) {
      if (validationRules.hasOwnProperty(key)) {
        const value = values[key];
        const fieldErrors = validateField(key, value);
        if (Object.keys(fieldErrors).length > 0) {
          formErrors[key] = fieldErrors[key];
        }
      }
    }
    return formErrors;
  };

  function resetField(field = {}) {
    const fields = {};
    if (field && typeof field == "object" && Object.keys(field)?.length > 0) {
      Object.keys(field)?.forEach((key) => {
        setValues((pre) => ({ ...pre, [key]: field[key] || "" }));
      });
      return;
    }
    if (Object?.keys(initialState)?.length > 0) {
      Object.keys(initialState)?.forEach((key) => {
        fields[key] = "";
      });
      setValues(fields);
      setErrors({});
    } else {
      setValues(null);
      setErrors(null);
    }
  }

  const formValidate = (errors, name, index, fieldKey = null) => {
    const error_obj = {};

    if (
      errors &&
      errors[name] &&
      name &&
      Object.keys(errors[name])?.length > 0
    ) {
      if (errors[name]?.type === "arrayValidation") {
        const err = errors[name]?.details?.find(
          (item) =>
            item.index === index && (!fieldKey || item.field === fieldKey)
        );

        if (err) {
          error_obj.errorType =
            err?.type === "required"
              ? "warning"
              : err?.type === "info"
              ? "info"
              : "danger";
          error_obj.errorText = err?.message;
        }
      } else {
        error_obj.errorType =
          errors[name]?.type === "required"
            ? "warning"
            : errors[name]?.type === "info"
            ? "info"
            : "danger";
        error_obj.errorText = errors[name]?.message;
      }
    }

    // Add required flag
    if (validationRules && validationRules?.[name]) {
      const rule = validationRules[name];
      if (Array.isArray(rule)) {
        error_obj.required = rule?.some((r) => r?.required);
      } else {
        error_obj.required = rule?.required;
      }
    }

    return error_obj;
  };

  const updateErrors = (data = {}) => {
    if (typeof data === "object" && !Array.isArray(data)) {
      Object.keys(data)?.forEach((key) => {
        setErrors((pre) => ({ ...pre, [key]: data[key] }));
      });
    } else {
      console.error("Update data is not an object");
    }
  };

  return {
    values,
    handleChange,
    handleSubmit,
    errors,
    setErrors,
    setFieldsValue,
    resetField,
    formValidate,
    updateErrors,
  };
};

export default useForm;
